/**
 * Solidify modifier — pure typed-array core (Three.js-free).
 *
 * Adds thickness by duplicating geometry as an offset shell along vertex
 * normals.
 *
 * When a vertex group is active, only faces whose every vertex belongs to
 * the group receive the duplicate shell and rim treatment.  The source shell
 * is always emitted in full.  Duplicate vertices are allocated compactly —
 * only vertices that participate in at least one included face receive a
 * counterpart, so no dead vertices appear in the output.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import {
  faceProvenanceFromSourceMap,
  provenanceFromSourceMap,
} from "../../modeling/provenance.ts";
import {
  buildEdgesAndCornerEdges,
  numEdgeKey,
} from "../../modeling/topology.ts";
import {
  asSharedTriangleIndices,
  type PolygonTopology,
  type SharedTriangleIndices,
} from "../../modeling/types.ts";
import { projectVertexGroupThroughProvenance } from "./helpers.ts";
import type { GeometryGroup, ModifierGeometry } from "./modifierTypes.ts";

/**
 * Compute area-weighted vertex normals from positions and indices.
 * Used as fallback when the geometry has no stored normals.
 */
function computeVertexNormalsFlat(
  positions: Float32Array,
  indices: SharedTriangleIndices,
  vertCount: number,
): Float32Array {
  const normals = new Float32Array(vertCount * 3);
  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i],
      ib = indices[i + 1],
      ic = indices[i + 2];
    const ax = ia * 3,
      bx = ib * 3,
      cx = ic * 3;
    const abx = positions[bx] - positions[ax];
    const aby = positions[bx + 1] - positions[ax + 1];
    const abz = positions[bx + 2] - positions[ax + 2];
    const acx = positions[cx] - positions[ax];
    const acy = positions[cx + 1] - positions[ax + 1];
    const acz = positions[cx + 2] - positions[ax + 2];
    const nx = aby * acz - abz * acy;
    const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx;
    normals[ax] += nx;
    normals[ax + 1] += ny;
    normals[ax + 2] += nz;
    normals[bx] += nx;
    normals[bx + 1] += ny;
    normals[bx + 2] += nz;
    normals[cx] += nx;
    normals[cx + 1] += ny;
    normals[cx + 2] += nz;
  }
  for (let i = 0; i < vertCount; i++) {
    const off = i * 3;
    const len =
      Math.sqrt(
        normals[off] ** 2 + normals[off + 1] ** 2 + normals[off + 2] ** 2,
      ) || 1;
    normals[off] /= len;
    normals[off + 1] /= len;
    normals[off + 2] /= len;
  }
  return normals;
}

/**
 * Compute per-vertex scaling factors for even shell thickness.
 * See solidify.ts for detailed documentation.
 */
function computeEvenThicknessScales(
  positions: Float32Array,
  normals: Float32Array,
  indices: SharedTriangleIndices,
  vertCount: number,
): Float32Array {
  const scales = new Float32Array(vertCount);

  if (indices.length === 0) {
    scales.fill(1);
    return scales;
  }

  const angleSum = new Float32Array(vertCount);

  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i],
      ib = indices[i + 1],
      ic = indices[i + 2];
    const ax = ia * 3,
      bx = ib * 3,
      cx = ic * 3;

    const abx = positions[bx] - positions[ax];
    const aby = positions[bx + 1] - positions[ax + 1];
    const abz = positions[bx + 2] - positions[ax + 2];
    const acx = positions[cx] - positions[ax];
    const acy = positions[cx + 1] - positions[ax + 1];
    const acz = positions[cx + 2] - positions[ax + 2];

    let fnx = aby * acz - abz * acy;
    let fny = abz * acx - abx * acz;
    let fnz = abx * acy - aby * acx;
    const fLen = Math.sqrt(fnx * fnx + fny * fny + fnz * fnz);
    if (fLen < 1e-10) continue;
    fnx /= fLen;
    fny /= fLen;
    fnz /= fLen;

    const verts = [ia, ib, ic];
    const px = [ax, bx, cx];

    for (let v = 0; v < 3; v++) {
      const vi = verts[v];
      const v0 = px[v];
      const v1 = px[(v + 1) % 3];
      const v2 = px[(v + 2) % 3];

      const e1x = positions[v1] - positions[v0];
      const e1y = positions[v1 + 1] - positions[v0 + 1];
      const e1z = positions[v1 + 2] - positions[v0 + 2];
      const e2x = positions[v2] - positions[v0];
      const e2y = positions[v2 + 1] - positions[v0 + 1];
      const e2z = positions[v2 + 2] - positions[v0 + 2];

      const len1 = Math.sqrt(e1x * e1x + e1y * e1y + e1z * e1z);
      const len2 = Math.sqrt(e2x * e2x + e2y * e2y + e2z * e2z);
      if (len1 < 1e-10 || len2 < 1e-10) continue;

      const cosAngle = (e1x * e2x + e1y * e2y + e1z * e2z) / (len1 * len2);
      const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle)));

      const nx = normals[v0],
        ny = normals[v0 + 1],
        nz = normals[v0 + 2];
      const dot = nx * fnx + ny * fny + nz * fnz;
      const absDot = Math.abs(dot);
      const dist = absDot > 1e-4 ? 1.0 / absDot : 1e4;

      scales[vi] += dist * angle;
      angleSum[vi] += angle;
    }
  }

  for (let i = 0; i < vertCount; i++) {
    if (angleSum[i] > 1e-10) {
      scales[i] /= angleSum[i];
      scales[i] = Math.max(0.25, Math.min(scales[i], 4.0));
    } else {
      scales[i] = 1.0;
    }
  }

  return scales;
}

export function solidifyCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "solidify" }>,
): ModifierGeometry {
  const positions = geo.positions;
  const vertCount = positions.length / 3;

  const normals =
    geo.normals ?? computeVertexNormalsFlat(positions, geo.indices, vertCount);

  const thickness = Math.abs(mod.offset);
  const direction = mod.offsetDirection;
  const duplicateIsOuter = direction > 0;

  // Compute per-vertex even-thickness scale factors.
  const vertScales = computeEvenThicknessScales(
    positions,
    normals,
    geo.indices,
    vertCount,
  );

  const allowed =
    projectVertexGroupThroughProvenance(
      mod.vertexGroupIndices,
      geo.provenance,
    ) ?? undefined;

  // ---------------------------------------------------------------------------
  // Determine which triangles receive the duplicate shell.
  // A triangle is included when all three of its vertices are in `allowed`,
  // or when no group is active (allowed === undefined).
  // ---------------------------------------------------------------------------
  const origIndices = geo.indices;
  const includedTris: number[] = [];
  if (origIndices.length > 0) {
    for (let i = 0; i < origIndices.length; i += 3) {
      if (
        !allowed ||
        (allowed.has(origIndices[i]) &&
          allowed.has(origIndices[i + 1]) &&
          allowed.has(origIndices[i + 2]))
      ) {
        includedTris.push(i);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Compact duplicate vertex map.
  // Only vertices that participate in at least one included face get an inner
  // counterpart, avoiding dead vertices in the output.
  // outerToInner[v] = compact inner index (0-based), or -1 if unused.
  // ---------------------------------------------------------------------------
  const outerToInner = new Int32Array(vertCount).fill(-1);
  let innerVertCount = 0;
  for (const ti of includedTris) {
    for (let j = 0; j < 3; j++) {
      const v = origIndices[ti + j];
      if (outerToInner[v] === -1) outerToInner[v] = innerVertCount++;
    }
  }

  // ---------------------------------------------------------------------------
  // Build output positions: [source 0..vertCount | compact duplicate vertices]
  // ---------------------------------------------------------------------------
  const newPos = new Float32Array(vertCount * 3 + innerVertCount * 3);
  newPos.set(positions, 0);

  for (let v = 0; v < vertCount; v++) {
    const k = outerToInner[v];
    if (k === -1) continue;
    const base = v * 3;
    const innerBase = k * 3;
    const scale = thickness * direction * vertScales[v];
    newPos[vertCount * 3 + innerBase] = positions[base] + normals[base] * scale;
    newPos[vertCount * 3 + innerBase + 1] =
      positions[base + 1] + normals[base + 1] * scale;
    newPos[vertCount * 3 + innerBase + 2] =
      positions[base + 2] + normals[base + 2] * scale;
  }

  // Build output UVs: [source UVs | compact duplicate UVs]
  const uvs = geo.uvs;
  let newUVs: Float32Array | null = null;
  if (uvs) {
    newUVs = new Float32Array(vertCount * 2 + innerVertCount * 2);
    newUVs.set(uvs, 0);
    for (let v = 0; v < vertCount; v++) {
      const k = outerToInner[v];
      if (k === -1) continue;
      newUVs[vertCount * 2 + k * 2] = uvs[v * 2];
      newUVs[vertCount * 2 + k * 2 + 1] = uvs[v * 2 + 1];
    }
  }

  // Build output morph deltas: same compact layout
  const newMorphArrays = geo.morphArrays.map((deltas) => {
    const merged = new Float32Array(vertCount * 3 + innerVertCount * 3);
    merged.set(deltas, 0);
    for (let v = 0; v < vertCount; v++) {
      const k = outerToInner[v];
      if (k === -1) continue;
      const src = v * 3;
      const dst = vertCount * 3 + k * 3;
      merged[dst] = deltas[src];
      merged[dst + 1] = deltas[src + 1];
      merged[dst + 2] = deltas[src + 2];
    }
    return merged;
  });

  // ---------------------------------------------------------------------------
  // Build output indices.
  // Rim edges: boundary of the included face set (edges adjacent to exactly
  // one included triangle).  This correctly handles both open-mesh boundary
  // edges within the group region AND edges shared with excluded faces.
  // ---------------------------------------------------------------------------
  if (origIndices.length > 0) {
    const edgeFaceCount = new Map<number, number>();
    const edgeVerts = new Map<number, [number, number]>();
    for (const ti of includedTris) {
      const a = origIndices[ti],
        b = origIndices[ti + 1],
        c = origIndices[ti + 2];
      for (const [v0, v1] of [
        [a, b],
        [b, c],
        [c, a],
      ] as [number, number][]) {
        const key = numEdgeKey(v0, v1);
        edgeFaceCount.set(key, (edgeFaceCount.get(key) ?? 0) + 1);
        if (!edgeVerts.has(key)) edgeVerts.set(key, [v0, v1]);
      }
    }

    const boundaryEdges: [number, number][] = [];
    for (const [key, count] of edgeFaceCount) {
      if (count === 1) boundaryEdges.push(edgeVerts.get(key)!);
    }

    const rimIndices: number[] = [];
    for (const [a, b] of boundaryEdges) {
      const iA = vertCount + outerToInner[a];
      const iB = vertCount + outerToInner[b];
      if (duplicateIsOuter) {
        rimIndices.push(a, b, iB, a, iB, iA);
      } else {
        rimIndices.push(a, iA, iB, a, iB, b);
      }
    }

    const innerIdxCount = includedTris.length * 3;
    const totalIdxCount =
      origIndices.length + innerIdxCount + rimIndices.length;
    const newIndices = new Uint32Array(totalIdxCount);

    // Source shell: unchanged when it remains outside, reversed when the
    // duplicate shell is outside and this surface becomes the inside wall.
    const includedTriSet = duplicateIsOuter ? new Set(includedTris) : null;
    for (let i = 0; i < origIndices.length; i += 3) {
      if (includedTriSet?.has(i)) {
        newIndices[i] = origIndices[i];
        newIndices[i + 1] = origIndices[i + 2];
        newIndices[i + 2] = origIndices[i + 1];
      } else {
        newIndices[i] = origIndices[i];
        newIndices[i + 1] = origIndices[i + 1];
        newIndices[i + 2] = origIndices[i + 2];
      }
    }

    // Duplicate shell: faces point outward from whichever side the thickness
    // was generated onto.
    let writePos = origIndices.length;
    for (const ti of includedTris) {
      if (duplicateIsOuter) {
        newIndices[writePos] = vertCount + outerToInner[origIndices[ti]];
        newIndices[writePos + 1] =
          vertCount + outerToInner[origIndices[ti + 1]];
        newIndices[writePos + 2] =
          vertCount + outerToInner[origIndices[ti + 2]];
      } else {
        newIndices[writePos] = vertCount + outerToInner[origIndices[ti]];
        newIndices[writePos + 1] =
          vertCount + outerToInner[origIndices[ti + 2]];
        newIndices[writePos + 2] =
          vertCount + outerToInner[origIndices[ti + 1]];
      }
      writePos += 3;
    }

    // Rim quads (two triangles per boundary edge)
    for (let i = 0; i < rimIndices.length; i++) {
      newIndices[writePos + i] = rimIndices[i];
    }

    // Groups: mirror the outer groups for the inner shell
    const newGroups: GeometryGroup[] = [];
    if (geo.groups.length > 0) {
      for (const g of geo.groups) {
        newGroups.push({
          start: g.start,
          count: g.count,
          materialIndex: g.materialIndex,
        });
      }
      const indexCount = origIndices.length;
      for (const g of geo.groups) {
        newGroups.push({
          start: g.start + indexCount,
          count: g.count,
          materialIndex: g.materialIndex,
        });
      }
    }

    // Build polygon topology when the input carries one
    let newTopology: PolygonTopology | undefined;
    let faceProvenance:
      | ReturnType<typeof faceProvenanceFromSourceMap>
      | undefined;
    if (geo.topology && origIndices.length > 0) {
      newTopology = buildSolidifyTopology(
        geo.topology,
        vertCount,
        outerToInner,
        allowed,
        duplicateIsOuter,
      );
      faceProvenance = faceProvenanceFromSourceMap(
        geo.topology.offsets.length - 1,
        buildSolidifyFaceSourceMap(geo.topology, allowed),
      );
    }

    // Provenance: outer verts at identity; inner verts descend from their
    // outer counterpart via the compact mapping.
    const outVertCount = vertCount + innerVertCount;
    const srcForNew = new Int32Array(outVertCount);
    for (let i = 0; i < vertCount; i++) srcForNew[i] = i;
    for (let v = 0; v < vertCount; v++) {
      const k = outerToInner[v];
      if (k !== -1) srcForNew[vertCount + k] = v;
    }
    const provenance = provenanceFromSourceMap(vertCount, srcForNew);

    return {
      positions: newPos,
      indices: asSharedTriangleIndices(newIndices),
      normals: null,
      uvs: newUVs,
      morphArrays: newMorphArrays,
      skinIndex: geo.skinIndex,
      skinWeight: geo.skinWeight,
      sculptDisplacement: geo.sculptDisplacement,
      groups: newGroups,
      morphTargetsRelative: geo.morphTargetsRelative,
      morphNames: geo.morphNames,
      // Wire edges and skin radii are not meaningful after solidify.
      edges: newTopology?.edges ?? null,
      edgeFlags: newTopology?.edgeFlags ?? null,
      edgeCreases: newTopology?.edgeCreases ?? null,
      skinRadii: null,
      topology: newTopology,
      faceProvenance,
      provenance,
    };
  }

  // Empty index case: no faces to solidify — return outer shell only with
  // identity provenance (innerVertCount is 0 here, so newPos = original positions).
  const idSrc = new Int32Array(vertCount);
  for (let i = 0; i < vertCount; i++) idSrc[i] = i;
  const provenance = provenanceFromSourceMap(vertCount, idSrc);
  return {
    positions: newPos,
    indices: origIndices,
    normals: null,
    uvs: newUVs,
    morphArrays: newMorphArrays,
    skinIndex: geo.skinIndex,
    skinWeight: geo.skinWeight,
    sculptDisplacement: geo.sculptDisplacement,
    groups: [],
    morphTargetsRelative: geo.morphTargetsRelative,
    morphNames: geo.morphNames,
    edges: null,
    edgeFlags: null,
    edgeCreases: null,
    skinRadii: null,
    topology: undefined,
    provenance,
  };
}

function includedSolidifyFaces(
  srcTopology: PolygonTopology,
  allowed: Set<number> | undefined,
): number[] {
  const { offsets, corners } = srcTopology;
  const faceCnt = offsets.length - 1;
  const includedFaces: number[] = [];
  for (let f = 0; f < faceCnt; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    if (allowed) {
      let allIn = true;
      for (let j = 0; j < size; j++) {
        if (!allowed.has(corners[start + j])) {
          allIn = false;
          break;
        }
      }
      if (!allIn) continue;
    }
    includedFaces.push(f);
  }
  return includedFaces;
}

function solidifyRimFaceCount(
  srcTopology: PolygonTopology,
  includedFaces: readonly number[],
): number {
  const { offsets, corners } = srcTopology;
  const edgeFaceCount = new Map<number, number>();
  for (const f of includedFaces) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) {
      const v0 = corners[start + j];
      const v1 = corners[start + ((j + 1) % size)];
      const key = numEdgeKey(v0, v1);
      edgeFaceCount.set(key, (edgeFaceCount.get(key) ?? 0) + 1);
    }
  }

  let count = 0;
  for (const edgeCount of edgeFaceCount.values()) {
    if (edgeCount === 1) count++;
  }
  return count;
}

function buildSolidifyFaceSourceMap(
  srcTopology: PolygonTopology,
  allowed: Set<number> | undefined,
): Int32Array {
  const faceCnt = srcTopology.offsets.length - 1;
  const includedFaces = includedSolidifyFaces(srcTopology, allowed);
  const rimCount = solidifyRimFaceCount(srcTopology, includedFaces);
  const out = new Int32Array(faceCnt + includedFaces.length + rimCount);
  out.fill(-1);
  for (let f = 0; f < faceCnt; f++) out[f] = f;
  for (let i = 0; i < includedFaces.length; i++) {
    out[faceCnt + i] = includedFaces[i];
  }
  return out;
}

/**
 * Build the polygon topology for the solidified output.
 *
 * Source shell: original faces remain unchanged when that side is outside,
 *   and reverse when the duplicate shell is outside.
 * Duplicate shell: only faces where every corner vertex is in `allowed`
 *   (or all faces when `allowed` is undefined), wound for the generated side
 *   and corner indices remapped to the compact inner vertex range.
 * Rim quads: one quad per boundary edge of the included face set.
 */
function buildSolidifyTopology(
  srcTopology: PolygonTopology,
  vertCount: number,
  outerToInner: Int32Array,
  allowed: Set<number> | undefined,
  duplicateIsOuter: boolean,
): PolygonTopology {
  const { offsets, corners } = srcTopology;
  const faceCnt = offsets.length - 1;

  // Determine which polygon faces are included in the duplicate shell.
  const includedFaces = includedSolidifyFaces(srcTopology, allowed);

  // Count corners in the included face set for sizing the duplicate shell.
  let includedCornerCount = 0;
  for (const f of includedFaces) {
    includedCornerCount += offsets[f + 1] - offsets[f];
  }

  // Rim edge detection: boundary edges of the included face set only.
  const edgeFaceCount2 = new Map<number, number>();
  const edgeVerts2 = new Map<number, [number, number]>();
  for (const f of includedFaces) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) {
      const v0 = corners[start + j];
      const v1 = corners[start + ((j + 1) % size)];
      const key = numEdgeKey(v0, v1);
      edgeFaceCount2.set(key, (edgeFaceCount2.get(key) ?? 0) + 1);
      if (!edgeVerts2.has(key)) edgeVerts2.set(key, [v0, v1]);
    }
  }
  const rimEdges: [number, number][] = [];
  for (const [key, cnt] of edgeFaceCount2) {
    if (cnt === 1) rimEdges.push(edgeVerts2.get(key)!);
  }

  const srcCornerCount = corners.length;
  const includedFaceCnt = includedFaces.length;
  const rimFaceCount = rimEdges.length;

  // Output face count: all source faces + included duplicate faces + rim quads
  const totalFaceCount = faceCnt + includedFaceCnt + rimFaceCount;
  const totalCornerCount =
    srcCornerCount + includedCornerCount + rimFaceCount * 4;

  const newOffsets = new Uint32Array(totalFaceCount + 1);
  const newCorners = new Uint32Array(totalCornerCount);

  // Source shell.
  const includedFaceSet = duplicateIsOuter ? new Set(includedFaces) : null;
  for (let f = 0; f < faceCnt; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    newOffsets[f] = start;
    if (includedFaceSet?.has(f)) {
      newCorners[start] = corners[start];
      for (let j = 1; j < size; j++) {
        newCorners[start + j] = corners[start + size - j];
      }
    } else {
      newCorners.set(corners.subarray(start, start + size), start);
    }
  }
  newOffsets[faceCnt] = srcCornerCount;

  // Duplicate shell.
  let cornerWrite = srcCornerCount;
  for (let i = 0; i < includedFaceCnt; i++) {
    const f = includedFaces[i];
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    if (duplicateIsOuter) {
      for (let j = 0; j < size; j++) {
        newCorners[cornerWrite + j] =
          vertCount + outerToInner[corners[start + j]];
      }
    } else {
      newCorners[cornerWrite] = vertCount + outerToInner[corners[start]];
      for (let j = 1; j < size; j++) {
        newCorners[cornerWrite + j] =
          vertCount + outerToInner[corners[start + size - j]];
      }
    }
    cornerWrite += size;
    newOffsets[faceCnt + i + 1] = cornerWrite;
  }

  // Rim quads: one quad per boundary edge connecting outer to inner shell
  for (let i = 0; i < rimFaceCount; i++) {
    const [a, b] = rimEdges[i];
    if (duplicateIsOuter) {
      newCorners[cornerWrite] = a;
      newCorners[cornerWrite + 1] = b;
      newCorners[cornerWrite + 2] = vertCount + outerToInner[b];
      newCorners[cornerWrite + 3] = vertCount + outerToInner[a];
    } else {
      newCorners[cornerWrite] = a;
      newCorners[cornerWrite + 1] = vertCount + outerToInner[a];
      newCorners[cornerWrite + 2] = vertCount + outerToInner[b];
      newCorners[cornerWrite + 3] = b;
    }
    cornerWrite += 4;
    newOffsets[faceCnt + includedFaceCnt + i + 1] = cornerWrite;
  }

  const { edges, cornerEdges, edgeFlags } = buildEdgesAndCornerEdges(
    newOffsets,
    newCorners,
  );
  return {
    offsets: newOffsets,
    corners: newCorners,
    edges,
    cornerEdges,
    edgeFlags,
  };
}
